import React, { useState, useMemo, useCallback } from 'react';

const RECENT_KEY = 'redmine-recent-projects';
const MAX_RECENT = 6;

function getRecentProjects() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function addRecentProject(projectId) {
  const recent = getRecentProjects().filter(id => id !== projectId);
  recent.unshift(projectId);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

function ProjectTree({ projects, onSelectProject }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [recentIds, setRecentIds] = useState(getRecentProjects);

  // Build tree structure
  const tree = useMemo(() => {
    const projectMap = new Map();
    const roots = [];

    // First pass: create map
    projects.forEach(p => {
      projectMap.set(p.id, { ...p, children: [] });
    });

    // Second pass: build tree
    projects.forEach(p => {
      const project = projectMap.get(p.id);
      if (p.parent) {
        const parent = projectMap.get(p.parent.id);
        if (parent) {
          parent.children.push(project);
        } else {
          roots.push(project);
        }
      } else {
        roots.push(project);
      }
    });

    // Sort alphabetically
    const sortProjects = (arr) => {
      arr.sort((a, b) => a.name.localeCompare(b.name));
      arr.forEach(p => sortProjects(p.children));
    };
    sortProjects(roots);

    return roots;
  }, [projects]);

  // Filter projects by search query
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree;

    const query = searchQuery.toLowerCase();

    const filterProject = (project) => {
      const matches = project.name.toLowerCase().includes(query) ||
                      project.identifier?.toLowerCase().includes(query);

      const filteredChildren = project.children
        .map(filterProject)
        .filter(Boolean);

      if (matches || filteredChildren.length > 0) {
        return {
          ...project,
          children: filteredChildren,
          _matched: matches
        };
      }

      return null;
    };

    return tree.map(filterProject).filter(Boolean);
  }, [tree, searchQuery]);

  // Toggle expansion
  const toggleExpand = (projectId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Expand all with children
  const expandAll = () => {
    const ids = new Set();
    const collectIds = (arr) => {
      arr.forEach(p => {
        if (p.children.length > 0) {
          ids.add(p.id);
          collectIds(p.children);
        }
      });
    };
    collectIds(tree);
    setExpandedIds(ids);
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Handle project selection with recent tracking
  const handleSelectProject = useCallback((projectId) => {
    addRecentProject(projectId);
    setRecentIds(getRecentProjects());
    onSelectProject(projectId);
  }, [onSelectProject]);

  // Recent projects list
  const recentProjects = useMemo(() => {
    if (searchQuery.trim()) return [];
    const projectMap = new Map(projects.map(p => [p.id, p]));
    return recentIds.map(id => projectMap.get(id)).filter(Boolean);
  }, [recentIds, projects, searchQuery]);

  // Render project node
  const renderProject = (project, depth = 0) => {
    const hasChildren = project.children.length > 0;
    const isExpanded = expandedIds.has(project.id);

    return (
      <div key={project.id} className="project-node">
        <div
          className={`project-item ${project._matched ? 'matched' : ''}`}
          style={{ paddingLeft: `${depth * 20 + 10}px` }}
        >
          {hasChildren ? (
            <button
              className="expand-btn"
              onClick={() => toggleExpand(project.id)}
            >
              {isExpanded ? '\u25BC' : '\u25B6'}
            </button>
          ) : (
            <span className="expand-placeholder"></span>
          )}

          <span
            className="project-name"
            onClick={() => handleSelectProject(project.id)}
          >
            {project.name}
          </span>

          <span className="project-identifier">
            {project.identifier}
          </span>
        </div>

        {hasChildren && isExpanded && (
          <div className="project-children">
            {project.children.map(child => renderProject(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="project-tree">
      <h2>Projects</h2>

      <div className="tree-toolbar">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter projects..."
        />
        <button className="tree-btn" onClick={expandAll}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          Expand All
        </button>
        <button className="tree-btn" onClick={collapseAll}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          Collapse All
        </button>
      </div>

      <p className="tree-hint">Click a project to view its issues</p>

      {recentProjects.length > 0 && (
        <div className="recent-projects">
          <h3 className="recent-title">Recently used</h3>
          <div className="recent-list">
            {recentProjects.map(p => (
              <button
                key={p.id}
                className="recent-chip"
                onClick={() => handleSelectProject(p.id)}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="tree-container">
        {filteredTree.length === 0 ? (
          <p className="no-projects">No projects found</p>
        ) : (
          filteredTree.map(project => renderProject(project))
        )}
      </div>
    </div>
  );
}

export default ProjectTree;
